// Mensajes de salida y recomendaciones.
//
// 1ª salida con una figura empezada → despedida ("tu diseño queda guardado 30
//   días, no lo dejes para muy tarde: cerca de las fechas el taller se llena").
// Vuelve al menos 12 h después y se va otra vez → invitación a recomendarnos:
//   su enlace personal (?ref=CÓDIGO). Si alguien hace un pedido desde él, se
//   le descuentan 5 € en su figura. El descuento lo aplica el taller a mano al
//   confirmar el pedido (en el admin se ve quién ha traído a quién).
//
// Antes la 2ª salida regalaba 5 € directamente; se cambió el 20-10-2026 porque
// salía a los pocos minutos y era un descuento sin contrapartida.
//
// Todo vive en el navegador del visitante (localStorage). Una "visita" es una
// pestaña/sesión (sessionStorage).

export const REFERRAL_AMOUNT = 5;
export const DRAFT_DAYS = 30;
export const DRAFT_MAX_AGE_MS = DRAFT_DAYS * 24 * 60 * 60 * 1000;
const MIN_HORAS_ENTRE_MENSAJES = 12;

const KEY = 'funcomunion_exit_state';
const VISIT_KEY = 'funcomunion_visit_id';
const DRAFT_KEY = 'funcomunion_wizard_draft';
const MI_CODIGO_KEY = 'funcomunion_ref_code';
const REFERIDO_POR_KEY = 'funcomunion_referido_por';

interface ExitState {
  goodbyeAt?: number;
  goodbyeVisit?: string;
  referralAt?: number;
}

export type ExitModalKind = 'goodbye' | 'referral';

function visitId(): string {
  try {
    let v = sessionStorage.getItem(VISIT_KEY);
    if (!v) {
      v = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(VISIT_KEY, v);
    }
    return v;
  } catch {
    return 'na';
  }
}

function read(): ExitState {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { return {}; }
}

function write(s: ExitState) {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* sin storage: sin mensajes */ }
}

/** ¿Hay una figura empezada de verdad (no solo elegido el tipo)? */
export function hasDraftInProgress(): boolean {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    if (!d?.config?.tipo || !d._savedAt || Date.now() - d._savedAt > DRAFT_MAX_AGE_MS) return false;
    return (d.pasoActual || 0) >= 2;
  } catch {
    return false;
  }
}

/** Qué mensaje toca al salir ahora (o null). No cambia nada. */
export function decideExitModal(): ExitModalKind | null {
  const s = read();
  if (!s.goodbyeAt) return 'goodbye';
  if (s.goodbyeVisit === visitId()) return null;
  if (s.referralAt) return null;
  if (Date.now() - s.goodbyeAt < MIN_HORAS_ENTRE_MENSAJES * 3600_000) return null;
  return 'referral';
}

/** Registra que se ha mostrado ese mensaje. */
export function markExitModalShown(kind: ExitModalKind) {
  const s = read();
  if (kind === 'goodbye') write({ ...s, goodbyeAt: Date.now(), goodbyeVisit: visitId() });
  else write({ ...s, referralAt: Date.now() });
}

// ── Recomendaciones ──

/** Código personal para recomendar (6 caracteres, sin 0/O/1/I para dictarlo). */
export function miCodigoReferido(): string {
  try {
    let c = localStorage.getItem(MI_CODIGO_KEY);
    if (!c) {
      const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      const r = crypto.getRandomValues(new Uint8Array(6));
      c = Array.from(r, b => abc[b % abc.length]).join('');
      localStorage.setItem(MI_CODIGO_KEY, c);
    }
    return c;
  } catch {
    return '';
  }
}

export function enlaceReferido(): string {
  const c = miCodigoReferido();
  return c ? `https://funcomunion.es/?ref=${c}` : 'https://funcomunion.es/';
}

/** Al entrar con ?ref=CÓDIGO se recuerda quién recomendó (si no es uno mismo). */
export function capturarReferido() {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref || !/^[A-Z0-9]{6}$/.test(ref)) return;
    if (ref === localStorage.getItem(MI_CODIGO_KEY)) return;
    localStorage.setItem(REFERIDO_POR_KEY, ref);
  } catch { /* */ }
}

export function referidoPor(): string | null {
  try { return localStorage.getItem(REFERIDO_POR_KEY); } catch { return null; }
}
